/**
 * A boss level's boss and its attacks (LevelData.boss, AttackObject).
 *
 * The boss — a red dragon flapping its wings, or a steel robot running on two legs with glowing
 * eyes, a laser cannon for an arm and a rocket pod on its back — is drawn behind the level, huge,
 * at the left of the view (see BossChase in game/boss.ts for where and how it moves): it rears
 * and opens its jaw, or aims its cannon and lights its eyes, as each attack's warning comes up,
 * and lets go as the attack goes off.
 *
 * Each attack is drawn where the physics has it at the view's moment of the level (attackBox,
 * the same function as the physics'), bigger than it kills, over the level: a fireball with its
 * tail, fire on the ground or a geyser out of it (tongues of flame), fire pouring down from the
 * sky, a laser beam from the robot's cannon across the view, a rocket with its flame. Before it,
 * its warning: a target on the ground where a fireball will land, a glowing vent, a red shimmer
 * where fire will pour, a blinking line where a beam will shine — and the stretch of the run the
 * racer will be on while it shines (x depends on the tick only): red on the ground under a low
 * beam (be in the air over it), shaded up to a high one (stay down under it) —, a «!» at the
 * view's edge where a rocket will come in; each with a «!» sign. Glows are pre-drawn sprites; no
 * shadowBlur.
 */
import {
  type AttackObject,
  attackBox,
  type BossKind,
  type Bounds,
  type LevelData,
  type World,
} from '@cube-rush/shared';
import type { BossPose } from '../game/boss';
import { rgba } from './color';
import { glowSprite } from './sprites';
import type { ViewTransform } from './view';

const TAU = Math.PI * 2;

/** The dragon's colours. */
const DRAGON = {
  body: '#b8322a',
  dark: '#6e1a1a',
  belly: '#f2a33a',
  wing: '#8e2a26',
  bone: '#4d1010',
  horn: '#f3e3c3',
  eye: '#ffd23f',
  ink: '#1a0606',
} as const;

/** The robot's colours. */
const ROBOT = {
  steel: '#9fb2c8',
  dark: '#55677f',
  darker: '#2e3a4c',
  ink: '#0d1118',
  eye: '#ff3b5c',
  core: '#2de2ff',
  accent: '#ffb238',
} as const;

/** Fire, from its edge to its core; a laser; a warning sign. */
const FIRE = ['#ff3d1f', '#ff8a1f', '#ffd23f', '#fff6c2'] as const;
const LASER = { glow: '#ff2d55', core: '#ffe3ea' } as const;
const WARNING = { fill: '#ffd60a', ink: '#2a0a0a', red: '#ff3b3b' } as const;

/** How long (ticks) the remains of an attack — its fire dying down, a beam's afterglow — show. */
const FADE_TICKS = 14;
/** A geyser's vent shows this long (ticks) before its warning. */
const VENT_TICKS = 240;

// ---------------------------------------------------------------------------
// Frames: local coordinates in blocks, y up
// ---------------------------------------------------------------------------

/**
 * Sets the context to draw in blocks, y up, from the world point (x, y), turned by `rot`
 * (counterclockwise). Line widths are in blocks too.
 */
function frame(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  scale: number,
  x: number,
  y: number,
  rot = 0,
): void {
  const k = view.ppb * scale;
  const cos = Math.cos(rot) * k;
  const sin = Math.sin(rot) * k;
  ctx.setTransform(cos, -sin, -sin, -cos, view.sx(x) * scale, view.sy(y) * scale);
}

function reset(ctx: CanvasRenderingContext2D, scale: number): void {
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
}

/** Fills and outlines the current path. */
function paint(ctx: CanvasRenderingContext2D, fill: string, ink: string, line: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = ink;
  ctx.lineWidth = line;
  ctx.stroke();
}

function ellipse(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number) {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, TAU);
}

/** A rounded box as a new path, through arcTo (ctx.roundRect is missing on older iOS). */
function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** A soft glow of `color`, `size` blocks across, at the world point (x, y) (screen space). */
function glow(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  x: number,
  y: number,
  size: number,
  color: string,
  alpha: number,
): void {
  const sprite = glowSprite(color);
  if (!sprite || alpha <= 0) return;
  const s = view.len(size);
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = Math.min(1, alpha);
  ctx.drawImage(sprite, view.sx(x) - s / 2, view.sy(y) - s / 2, s, s);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------
// The boss
// ---------------------------------------------------------------------------

/** World point of the robot's cannon's muzzle, or the dragon's jaw: where its attacks come from. */
export function bossMuzzle(
  pose: BossPose,
  out: { x: number; y: number },
): { x: number; y: number } {
  if (pose.kind === 'dragon') {
    out.x = pose.x - 0.2;
    out.y = DRAGON_Y + HEAD_Y - 0.15 + dragonBob(pose);
  } else {
    out.x = pose.x + 0.55;
    out.y = robotAim(pose);
  }
  return out;
}

/**
 * Height of the dragon's body above the ground (its middle; its head over a cube's jump) and how
 * far it bobs; where its head is on its body, its snout's tip at the pose's x.
 */
const DRAGON_Y = 3;
const HEAD_X = 1.95;
const HEAD_Y = 1.5;
const SNOUT = 1.25;

function dragonBob(pose: BossPose): number {
  return 0.22 * Math.sin(pose.run * 0.55);
}

/** Where the robot's arm aims: at the beam it is about to fire, else down by its side. */
function robotAim(pose: BossPose): number {
  const a = pose.attack;
  const target = a?.attack === 'laser' ? a.y + a.h / 2 : 2.2;
  const t = Math.max(pose.charge, pose.fire);
  return 2.2 + (target - 2.2) * Math.min(1, t * 1.6);
}

/**
 * The boss in its pose (see BossPose): behind the level, at the left of the view; it tumbles
 * and fades as it crashes behind (`crash`).
 */
export function drawBoss(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  scale: number,
  pose: BossPose,
  time: number,
): void {
  if (pose.x < view.left - 3 || pose.x - 9 > view.right) return;
  // Crashing: tipping over and sinking, then gone.
  const crash = pose.crash;
  const alpha = 1 - crash * crash;
  if (alpha <= 0.02) return;
  ctx.globalAlpha = alpha;
  if (pose.kind === 'dragon') drawDragon(ctx, view, scale, pose, time, crash);
  else drawRobot(ctx, view, scale, pose, crash);
  reset(ctx, scale);
  ctx.globalAlpha = 1;
}

/**
 * The dragon, its snout at the pose's x: a body with a spiked back and a tail reaching off the
 * view, two wings beating up over it (the far one behind it, a beat later), a neck curving up to a
 * horned head whose jaw opens as it draws breath; fire from the jaw as an attack goes off.
 */
function drawDragon(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  scale: number,
  pose: BossPose,
  time: number,
  crash: number,
): void {
  const beat = time * 7.5;
  const bob = dragonBob(pose);
  // Origin: the middle of its body.
  const ox = pose.x - HEAD_X - SNOUT;
  const oy = DRAGON_Y + bob - crash * 2.6;
  // Rearing up as it draws breath, ducking its head as it lets go; nose down as it crashes.
  const lean = 0.1 * pose.charge - 0.06 * pose.fire - crash * 0.9;
  // Its glow, so it stands out on every backdrop.
  glow(ctx, view, ox + 0.8, oy + 0.8, 7, DRAGON.body, 0.35);
  frame(ctx, view, scale, ox, oy, lean);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const line = 0.08;
  // The far wing, behind everything, a beat later.
  wing(ctx, 0.45, 0.95, 0.1 + 0.55 * Math.sin(beat - 0.6), DRAGON.dark, line);
  // The tail, off to the left, with spikes along it.
  ctx.beginPath();
  ctx.moveTo(-1.3, 0.45);
  ctx.bezierCurveTo(-3.0, 0.8, -4.2, -0.7, -6.4, -0.3);
  ctx.lineTo(-6.5, -0.65);
  ctx.bezierCurveTo(-4.2, -1.2, -3.0, -0.1, -1.2, -0.45);
  ctx.closePath();
  paint(ctx, DRAGON.body, DRAGON.ink, line);
  // The body.
  ellipse(ctx, 0, 0, 1.75, 1.05);
  paint(ctx, DRAGON.body, DRAGON.ink, line);
  ellipse(ctx, 0.3, -0.4, 1.25, 0.55);
  ctx.fillStyle = DRAGON.belly;
  ctx.fill();
  // Spikes along its back.
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const x = -1.3 + i * 0.6;
    const y = 0.92 - Math.abs(x) * 0.2;
    ctx.moveTo(x - 0.18, y - 0.05);
    ctx.lineTo(x, y + 0.38);
    ctx.lineTo(x + 0.18, y - 0.05);
  }
  paint(ctx, DRAGON.horn, DRAGON.ink, 0.05);
  // A leg, tucked under.
  ctx.beginPath();
  ctx.moveTo(0.5, -0.8);
  ctx.quadraticCurveTo(0.8, -1.45, 1.25, -1.35);
  ctx.lineWidth = 0.32;
  ctx.strokeStyle = DRAGON.ink;
  ctx.stroke();
  ctx.lineWidth = 0.2;
  ctx.strokeStyle = DRAGON.dark;
  ctx.stroke();
  // The neck, curving up to the head.
  ctx.beginPath();
  ctx.moveTo(0.85, 0.8);
  ctx.bezierCurveTo(1.4, 1.5, 1.6, 1.85, HEAD_X + 0.05, HEAD_Y + 0.3);
  ctx.lineTo(HEAD_X + 0.15, HEAD_Y - 0.4);
  ctx.bezierCurveTo(1.8, 0.9, 1.6, 0.4, 1.3, -0.3);
  ctx.closePath();
  paint(ctx, DRAGON.body, DRAGON.ink, line);
  drawDragonHead(ctx, view, scale, pose, time, line, ox, oy, lean);
  // The near wing, in front of the body.
  frame(ctx, view, scale, ox, oy, lean);
  wing(ctx, 0.8, 0.85, 0.05 + 0.55 * Math.sin(beat), DRAGON.wing, line);
}

/**
 * A bat's wing from its shoulder at (x, y), turned back by `angle` (radians: about 0 straight up,
 * 1 down behind it): three fingers from the elbow and the membrane between them, sagging between
 * the fingers.
 */
function wing(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  fill: string,
  line: number,
): void {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // The points round the shoulder, turned (plain numbers: no garbage per frame).
  const ex = turnedX(x, cos, sin, 0.5, 0.9);
  const ey = turnedY(y, cos, sin, 0.5, 0.9);
  const ax = turnedX(x, cos, sin, 0.75, 2.9);
  const ay = turnedY(y, cos, sin, 0.75, 2.9);
  const bx = turnedX(x, cos, sin, -0.5, 2.7);
  const by = turnedY(y, cos, sin, -0.5, 2.7);
  const cx = turnedX(x, cos, sin, -1.45, 1.8);
  const cy = turnedY(y, cos, sin, -1.45, 1.8);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(ex, ey);
  ctx.lineTo(ax, ay);
  ctx.quadraticCurveTo(turnedX(x, cos, sin, 0.05, 2.35), turnedY(y, cos, sin, 0.05, 2.35), bx, by);
  ctx.quadraticCurveTo(
    turnedX(x, cos, sin, -0.75, 1.85),
    turnedY(y, cos, sin, -0.75, 1.85),
    cx,
    cy,
  );
  ctx.quadraticCurveTo(
    turnedX(x, cos, sin, -1.0, 0.7),
    turnedY(y, cos, sin, -1.0, 0.7),
    turnedX(x, cos, sin, -0.5, 0.05),
    turnedY(y, cos, sin, -0.5, 0.05),
  );
  ctx.closePath();
  paint(ctx, fill, DRAGON.ink, line);
  ctx.beginPath();
  ctx.moveTo(ex, ey);
  ctx.lineTo(ax, ay);
  ctx.moveTo(ex, ey);
  ctx.lineTo(bx, by);
  ctx.moveTo(ex, ey);
  ctx.lineTo(cx, cy);
  ctx.strokeStyle = DRAGON.bone;
  ctx.lineWidth = 0.07;
  ctx.stroke();
}

/** x of the point (u, v) round (x, ·), turned by the angle whose cosine and sine are given. */
function turnedX(x: number, cos: number, sin: number, u: number, v: number): number {
  return x + u * cos - v * sin;
}

function turnedY(y: number, cos: number, sin: number, u: number, v: number): number {
  return y + u * sin + v * cos;
}

/** The dragon's head: horns, a glowing eye, a jaw that opens as it draws breath and breathes fire. */
function drawDragonHead(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  scale: number,
  pose: BossPose,
  time: number,
  line: number,
  ox: number,
  oy: number,
  lean: number,
): void {
  const open = Math.max(pose.charge * 0.55, pose.fire * 0.65);
  // The head turns up a little as it draws breath (its own frame round the back of the skull).
  const hx = HEAD_X;
  const hy = HEAD_Y;
  frame(ctx, view, scale, ox, oy, lean);
  ctx.translate(hx, hy);
  ctx.rotate(0.15 * pose.charge);
  // Horns.
  ctx.beginPath();
  ctx.moveTo(-0.2, 0.35);
  ctx.quadraticCurveTo(-0.6, 0.9, -1.0, 0.95);
  ctx.quadraticCurveTo(-0.55, 0.6, -0.45, 0.15);
  ctx.closePath();
  ctx.moveTo(0.15, 0.42);
  ctx.quadraticCurveTo(-0.15, 1.05, -0.5, 1.2);
  ctx.quadraticCurveTo(-0.15, 0.8, -0.1, 0.25);
  ctx.closePath();
  paint(ctx, DRAGON.horn, DRAGON.ink, 0.05);
  // The lower jaw, opening.
  ctx.save();
  ctx.translate(0.05, -0.15);
  ctx.rotate(-open);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(1.0, -0.12);
  ctx.quadraticCurveTo(1.05, -0.32, 0.85, -0.34);
  ctx.lineTo(0.05, -0.38);
  ctx.closePath();
  paint(ctx, DRAGON.dark, DRAGON.ink, line);
  ctx.restore();
  // Fire glowing in the open mouth.
  if (open > 0.05) {
    ctx.beginPath();
    ctx.moveTo(0.15, -0.16);
    ctx.lineTo(0.95, -0.14);
    ctx.lineTo(0.95, -0.18 - open * 0.9);
    ctx.closePath();
    ctx.fillStyle = FIRE[1];
    ctx.fill();
  }
  // The skull and the snout.
  ctx.beginPath();
  ctx.moveTo(-0.45, -0.25);
  ctx.quadraticCurveTo(-0.5, 0.45, 0.1, 0.5);
  ctx.quadraticCurveTo(0.55, 0.5, 0.75, 0.25);
  ctx.lineTo(1.1, 0.12);
  ctx.quadraticCurveTo(1.25, 0.02, 1.1, -0.12);
  ctx.lineTo(0.05, -0.18);
  ctx.closePath();
  paint(ctx, DRAGON.body, DRAGON.ink, line);
  // A nostril, and the eye: brighter as it draws breath.
  ellipse(ctx, 0.98, 0.08, 0.05, 0.04);
  ctx.fillStyle = DRAGON.ink;
  ctx.fill();
  ellipse(ctx, 0.28, 0.24, 0.13, 0.09);
  ctx.fillStyle = DRAGON.eye;
  ctx.fill();
  ctx.fillStyle = DRAGON.ink;
  ctx.fillRect(0.26, 0.16, 0.04, 0.16);
  reset(ctx, scale);
  const eye = Math.max(pose.charge, pose.fire);
  if (eye > 0) {
    glow(ctx, view, ox + hx + 0.3, oy + hy + 0.25, 1.1, DRAGON.eye, 0.8 * eye);
  }
  // Breathing fire as an attack goes off (a short puff: it never reaches the player).
  if (pose.fire > 0) {
    const mouthX = ox + hx + 1.05;
    const mouthY = oy + hy - 0.2;
    const length = 0.9 + 0.5 * Math.sin(time * 40) * 0.2;
    puff(ctx, view, mouthX, mouthY, length * pose.fire, 0.35 * pose.fire, time);
  }
}

/** A puff of fire from (x, y) forward `length` blocks, `width` thick at its end. */
function puff(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  x: number,
  y: number,
  length: number,
  width: number,
  time: number,
): void {
  for (let layer = 0; layer < 3; layer++) {
    const shrink = 1 - layer * 0.3;
    const wobble = Math.sin(time * 31 + layer * 2) * 0.05;
    ctx.beginPath();
    ctx.moveTo(view.sx(x), view.sy(y));
    ctx.quadraticCurveTo(
      view.sx(x + length * 0.6 * shrink),
      view.sy(y + width * shrink + wobble),
      view.sx(x + length * shrink),
      view.sy(y + wobble),
    );
    ctx.quadraticCurveTo(
      view.sx(x + length * 0.6 * shrink),
      view.sy(y - width * shrink - wobble),
      view.sx(x),
      view.sy(y),
    );
    ctx.fillStyle = FIRE[layer + 1]!;
    ctx.fill();
  }
  glow(ctx, view, x + length * 0.5, y, length * 1.8, FIRE[1], 0.6);
}

/**
 * The robot, its chest's front at the pose's x: two legs running with the racer, a boxy torso
 * with a glowing core, a head with a visor and two eyes that flare as it aims, a rocket pod on its
 * back (its tubes glow before a rocket comes), an arm with a laser cannon aimed at the beam it is
 * about to fire.
 */
function drawRobot(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  scale: number,
  pose: BossPose,
  crash: number,
): void {
  const ox = pose.x - 1.6;
  const stride = (pose.run / 2.8) * TAU;
  const step = Math.abs(Math.sin(stride)) * 0.18;
  // Falling over backward (away from the racer) as it crashes.
  const tilt = crash * 1.1;
  glow(ctx, view, ox - 0.4, 2.8, 7, ROBOT.core, 0.18);
  frame(ctx, view, scale, ox, -crash * 1.5, tilt);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const line = 0.08;
  // Legs: thigh and shin, the far one darker, running half a step apart.
  for (let leg = 0; leg < 2; leg++) {
    const p = stride + leg * Math.PI;
    const hipX = -0.9 + (leg === 0 ? -0.25 : 0.25);
    const hipY = 2.1 + step;
    const footX = hipX + Math.sin(p) * 0.9;
    const footY = Math.max(0, Math.cos(p)) * 0.45;
    const kneeX = (hipX + footX) / 2 + 0.35;
    const kneeY = (hipY + footY) / 2;
    const color = leg === 0 ? ROBOT.darker : ROBOT.dark;
    ctx.beginPath();
    ctx.moveTo(hipX, hipY);
    ctx.lineTo(kneeX, kneeY);
    ctx.lineTo(footX, footY + 0.2);
    ctx.strokeStyle = ROBOT.ink;
    ctx.lineWidth = 0.5;
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = 0.34;
    ctx.stroke();
    box(ctx, footX - 0.35, footY, 0.85, 0.28, 0.08);
    paint(ctx, ROBOT.darker, ROBOT.ink, line);
    ctx.beginPath();
    ctx.arc(kneeX, kneeY, 0.18, 0, TAU);
    paint(ctx, ROBOT.steel, ROBOT.ink, 0.05);
  }
  // The rocket pod on its back: its tubes glow before a rocket.
  const rockets = pose.attack?.attack === 'rocket' ? Math.max(pose.charge, pose.fire) : 0;
  box(ctx, -2.65, 3.5 + step, 1.0, 1.35, 0.12);
  paint(ctx, ROBOT.dark, ROBOT.ink, line);
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.arc(-2.15, 3.75 + step + i * 0.4, 0.13, 0, TAU);
    ctx.fillStyle = rockets > 0 ? ROBOT.accent : ROBOT.ink;
    ctx.fill();
  }
  // The torso and its core.
  box(ctx, -1.95, 2.0 + step, 2.0, 2.2, 0.3);
  paint(ctx, ROBOT.steel, ROBOT.ink, line);
  ctx.beginPath();
  ctx.moveTo(-1.95, 3.1 + step);
  ctx.lineTo(0.05, 3.1 + step);
  ctx.strokeStyle = ROBOT.dark;
  ctx.lineWidth = 0.05;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-0.75, 2.6 + step, 0.28, 0, TAU);
  paint(ctx, ROBOT.core, ROBOT.ink, 0.05);
  // The head: a box with a visor and its eyes.
  box(ctx, -1.55, 4.25 + step, 1.35, 1.05, 0.2);
  paint(ctx, ROBOT.steel, ROBOT.ink, line);
  box(ctx, -1.0, 4.55 + step, 0.85, 0.42, 0.1);
  ctx.fillStyle = ROBOT.ink;
  ctx.fill();
  const eyes = 0.35 + 0.65 * Math.max(pose.charge, pose.fire);
  ctx.fillStyle = rgba(ROBOT.eye, eyes);
  ctx.fillRect(-0.9, 4.68 + step, 0.26, 0.14);
  ctx.fillRect(-0.52, 4.68 + step, 0.26, 0.14);
  // An antenna with its light.
  ctx.beginPath();
  ctx.moveTo(-1.15, 5.3 + step);
  ctx.lineTo(-1.3, 5.75 + step);
  ctx.strokeStyle = ROBOT.ink;
  ctx.lineWidth = 0.07;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-1.3, 5.8 + step, 0.1, 0, TAU);
  ctx.fillStyle = ROBOT.eye;
  ctx.fill();
  // The arm and its cannon, aimed (see robotAim): shoulder, elbow, the cannon level at the aim.
  const aim = robotAim(pose) + crash * 1.5;
  const shoulderX = -0.4;
  const shoulderY = 3.75 + step;
  const muzzleX = 2.15;
  const elbowX = (shoulderX + muzzleX - 1.1) / 2;
  const elbowY = Math.min(shoulderY, aim) - 0.35;
  ctx.beginPath();
  ctx.moveTo(shoulderX, shoulderY);
  ctx.lineTo(elbowX, elbowY);
  ctx.lineTo(muzzleX - 1.1, aim);
  ctx.strokeStyle = ROBOT.ink;
  ctx.lineWidth = 0.46;
  ctx.stroke();
  ctx.strokeStyle = ROBOT.dark;
  ctx.lineWidth = 0.3;
  ctx.stroke();
  box(ctx, muzzleX - 1.25, aim - 0.24, 1.25, 0.48, 0.12);
  paint(ctx, ROBOT.darker, ROBOT.ink, line);
  ctx.beginPath();
  ctx.arc(shoulderX, shoulderY, 0.3, 0, TAU);
  paint(ctx, ROBOT.steel, ROBOT.ink, 0.06);
  reset(ctx, scale);
  // Eyes and the cannon's muzzle glow as it aims.
  const charge = Math.max(pose.charge, pose.fire);
  if (charge > 0) {
    glow(ctx, view, ox - 0.6, 4.75 + step - crash * 1.5, 1.4, ROBOT.eye, 0.7 * charge);
    if (pose.attack?.attack === 'laser') {
      glow(ctx, view, pose.x + 0.55, aim, 1.2 + charge, LASER.glow, 0.9 * charge);
    }
  }
}

// ---------------------------------------------------------------------------
// The attacks
// ---------------------------------------------------------------------------

/**
 * Which flames of a level are the fire a fireball leaves where it lands (they share its warning:
 * the fireball's target shows it), as attacks' indices in the level's World.
 */
export function landingFires(attacks: readonly AttackObject[]): Set<number> {
  const fires = new Set<number>();
  const balls = attacks.filter((a) => a.attack === 'fireball');
  attacks.forEach((a, i) => {
    if (a.attack !== 'flame') return;
    const mid = a.x + a.w / 2;
    for (const b of balls) {
      const lands = b.x + (b.dx ?? 0) + b.w / 2;
      if (b.to === a.from && Math.abs(lands - mid) < 0.01) fires.add(i);
    }
  });
  return fires;
}

const landingCache = new WeakMap<LevelData, Set<number>>();

/** landingFires of a level's World, found once per level. */
function landingOf(level: LevelData, world: World): ReadonlySet<number> {
  let fires = landingCache.get(level);
  if (!fires) {
    fires = landingFires(world.attacks);
    landingCache.set(level, fires);
  }
  return fires;
}

/** Scratch for the attacks' boxes (no garbage per frame). */
const BOX: Bounds = { x1: 0, y1: 0, x2: 0, y2: 0 };
const BEFORE: Bounds = { x1: 0, y1: 0, x2: 0, y2: 0 };

/** What the attack layer needs to know this frame. */
export interface AttackDrawState {
  /** The view's moment of the level (fractional tick). */
  tick: number;
  /** Real time, s (flicker). */
  time: number;
  /** Where a laser beam starts: the robot's muzzle (world x), or null without it in view. */
  muzzleX: number | null;
  /**
   * The view's racer's run: where its centre is at a tick of the attempt (see LevelGuide.xAt), so
   * a beam's warning marks where the racer will be while it shines; null: no racer.
   */
  run: { xAt(tick: number): number } | null;
}

/**
 * The attacks of a level in view (its World's): their warnings, themselves while they are there,
 * their remains for a moment after.
 */
export function drawAttacks(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  level: LevelData,
  world: World,
  state: AttackDrawState,
): void {
  if (world.attacks.length === 0) return;
  const { tick } = state;
  const landing = landingOf(level, world);
  const index = world.attackIndex;
  const n = index.query(view.left - 1, view.right + 1);
  for (let k = 0; k < n; k++) {
    const id = index.found[k]!;
    if (world.attackFrom[id]! > view.right + 1 || world.attackTo[id]! < view.left - 1) continue;
    const a = world.attacks[id]!;
    if (tick >= a.to + FADE_TICKS) continue;
    // (A fireball's target stays until it lands.)
    const warning = a.attack === 'fireball' ? a.to : a.from;
    if (tick < warning) {
      const vent = a.attack === 'flame' && a.grows !== 'down' && tick >= a.warn - VENT_TICKS;
      if (tick >= a.warn || vent) drawWarning(ctx, view, a, state, landing.has(id));
      if (tick < a.from) continue;
    }
    switch (a.attack) {
      case 'fireball':
        if (tick < a.to) drawFireball(ctx, view, a, tick, state.time);
        break;
      case 'flame':
        drawFlame(ctx, view, a, tick, state.time);
        break;
      case 'laser':
        drawLaser(ctx, view, a, state);
        break;
      case 'rocket':
        if (tick < a.to) drawRocket(ctx, view, a, tick, state.time);
        break;
    }
  }
}

/** How strongly a warning blinks now: faster as its attack comes (0.2 … 1). */
function blink(a: AttackObject, tick: number, time: number): number {
  const at = a.attack === 'fireball' ? a.to : a.from;
  const left = Math.max(0, (at - tick) / Math.max(1, at - a.warn));
  const speed = 6 + 14 * (1 - left);
  return 0.6 + 0.4 * Math.sin(time * speed);
}

function drawWarning(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  a: AttackObject,
  state: AttackDrawState,
  landing: boolean,
): void {
  const { tick, time } = state;
  const shown = tick >= a.warn;
  const pulse = shown ? blink(a, tick, time) : 0;
  switch (a.attack) {
    case 'fireball': {
      // A target on the ground where it will land.
      const x = a.x + (a.dx ?? 0) + a.w / 2;
      target(ctx, view, x, 0, 0.75, pulse);
      sign(ctx, view, x, 2.0, pulse);
      break;
    }
    case 'flame': {
      if (landing) break;
      const mid = a.x + a.w / 2;
      if (a.grows === 'down') {
        // A red shimmer where the fire will pour down, the ground below it lit.
        const x1 = view.sx(a.x);
        const x2 = view.sx(a.x + a.w);
        const bottom = view.sy(a.y);
        ctx.fillStyle = rgba(WARNING.red, 0.12 + 0.12 * pulse);
        ctx.fillRect(x1, view.y, x2 - x1, bottom - view.y);
        ctx.setLineDash([view.len(0.25), view.len(0.2)]);
        ctx.strokeStyle = rgba(WARNING.red, 0.5 + 0.4 * pulse);
        ctx.lineWidth = Math.max(1.5, view.len(0.06));
        ctx.beginPath();
        ctx.moveTo(x1, view.y);
        ctx.lineTo(x1, bottom);
        ctx.lineTo(x2, bottom);
        ctx.lineTo(x2, view.y);
        ctx.stroke();
        ctx.setLineDash([]);
        sign(ctx, view, mid, a.y - 0.7, pulse);
      } else {
        // A vent in the ground: dark, then glowing and smoking as its fire comes.
        vent(ctx, view, mid, a.w, pulse);
        if (shown) sign(ctx, view, mid, Math.max(1.6, a.h + 1), pulse);
      }
      break;
    }
    case 'laser': {
      // A thin blinking line where the beam will shine.
      const y = view.sy(a.y + a.h / 2);
      const from = Math.max(view.x, view.sx(Math.max(a.x, state.muzzleX ?? a.x)));
      const to = Math.min(view.x + view.w, view.sx(a.x + a.w));
      if (to <= from) break;
      ctx.setLineDash([view.len(0.4), view.len(0.25)]);
      ctx.strokeStyle = rgba(LASER.glow, 0.35 + 0.5 * pulse);
      ctx.lineWidth = Math.max(1.5, view.len(0.05));
      ctx.beginPath();
      ctx.moveTo(from, y);
      ctx.lineTo(to, y);
      ctx.stroke();
      ctx.setLineDash([]);
      if (state.run) laserStretch(ctx, view, a, state.run, pulse);
      sign(ctx, view, view.left + (view.right - view.left) * 0.86, a.y + a.h / 2 + 0.75, pulse);
      break;
    }
    case 'rocket': {
      // A «!» at the view's right edge, at the height it will come in.
      if (a.x <= view.right - 0.6) break;
      const y = a.y + a.h / 2;
      sign(ctx, view, view.right - 0.75, y, pulse);
      break;
    }
  }
}

/** A beam whose middle is lower than this jumps like a skipping rope; a higher one is run under. */
const LOW_BEAM = 1;
/** A standing cube's head, and the glowing strip on the ground under a low beam (blocks). */
const HEAD = 1.05;
const STRIP = 0.24;

/**
 * Where the racer will be while a beam shines (its centre from tick `from` to `to`, its box half a
 * block either way), as the beam's warning comes: a low beam's stretch a glowing red strip on the
 * ground — be in the air over it as the racer is there —, a high one's shaded from a standing
 * cube's head up to the beam — stay down under it.
 */
function laserStretch(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  a: AttackObject,
  run: { xAt(tick: number): number },
  pulse: number,
): void {
  const from = run.xAt(a.from) - 0.5;
  const to = run.xAt(a.to) + 0.5;
  const x1 = view.sx(from);
  const x2 = view.sx(to);
  if (x2 < view.x || x1 > view.x + view.w || x2 <= x1) return;
  const beam = a.y + a.h / 2;
  if (beam < LOW_BEAM) {
    const top = view.sy(STRIP);
    ctx.fillStyle = rgba(WARNING.red, 0.55 + 0.4 * pulse);
    ctx.fillRect(x1, top, x2 - x1, view.sy(0) - top);
    ctx.fillStyle = rgba(WARNING.fill, 0.45 + 0.5 * pulse);
    ctx.fillRect(x1, top, x2 - x1, Math.max(1.5, view.len(0.06)));
    glow(ctx, view, (from + to) / 2, STRIP / 2, to - from + 1, WARNING.red, 0.4 * pulse);
    return;
  }
  const top = view.sy(beam);
  const bottom = view.sy(HEAD);
  if (bottom <= top) return;
  ctx.fillStyle = rgba(WARNING.red, 0.2 + 0.18 * pulse);
  ctx.fillRect(x1, top, x2 - x1, bottom - top);
  ctx.setLineDash([view.len(0.2), view.len(0.15)]);
  ctx.strokeStyle = rgba(WARNING.red, 0.45 + 0.4 * pulse);
  ctx.lineWidth = Math.max(1.5, view.len(0.05));
  ctx.beginPath();
  ctx.moveTo(x1, top);
  ctx.lineTo(x1, bottom);
  ctx.lineTo(x2, bottom);
  ctx.lineTo(x2, top);
  ctx.stroke();
  ctx.setLineDash([]);
}

/** A target ring flat on the ground at (x, y): it pulses. */
function target(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  x: number,
  y: number,
  r: number,
  pulse: number,
): void {
  const cx = view.sx(x);
  const cy = view.sy(y);
  for (let k = 0; k < 2; k++) {
    const rr = view.len(r * (k === 0 ? 1 : 0.5) * (0.85 + 0.15 * pulse));
    ctx.beginPath();
    ctx.ellipse(cx, cy, rr, rr * 0.3, 0, 0, TAU);
    ctx.strokeStyle = rgba(k === 0 ? WARNING.red : WARNING.fill, 0.55 + 0.4 * pulse);
    ctx.lineWidth = Math.max(1.5, view.len(0.07));
    ctx.stroke();
  }
  glow(ctx, view, x, y, r * 3, WARNING.red, 0.35 * pulse);
}

/** A vent in the ground at x, `w` wide, glowing with `heat` (0: cold). */
function vent(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  x: number,
  w: number,
  heat: number,
) {
  const half = view.len(Math.max(0.35, w * 0.9));
  const cx = view.sx(x);
  const cy = view.sy(0);
  ctx.fillStyle = '#140808';
  ctx.beginPath();
  ctx.ellipse(cx, cy, half, Math.max(2, half * 0.28), 0, 0, TAU);
  ctx.fill();
  if (heat > 0) {
    ctx.fillStyle = rgba(FIRE[1], 0.4 + 0.5 * heat);
    ctx.beginPath();
    ctx.ellipse(cx, cy, half * 0.65, Math.max(1.5, half * 0.16), 0, 0, TAU);
    ctx.fill();
    glow(ctx, view, x, 0.15, 1.6, FIRE[0], 0.5 * heat);
  }
}

/** A warning sign: «!» in a yellow triangle at (x, y), pulsing with `pulse`. */
function sign(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  x: number,
  y: number,
  pulse: number,
) {
  const s = Math.max(9, view.len(0.42)) * (0.92 + 0.08 * pulse);
  const cx = view.sx(x);
  const cy = view.sy(y);
  ctx.globalAlpha = 0.55 + 0.45 * pulse;
  ctx.beginPath();
  ctx.moveTo(cx, cy - s);
  ctx.lineTo(cx + s * 0.95, cy + s * 0.7);
  ctx.lineTo(cx - s * 0.95, cy + s * 0.7);
  ctx.closePath();
  ctx.fillStyle = WARNING.fill;
  ctx.fill();
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(1.5, s * 0.14);
  ctx.strokeStyle = WARNING.ink;
  ctx.stroke();
  ctx.fillStyle = WARNING.ink;
  ctx.fillRect(cx - s * 0.08, cy - s * 0.48, s * 0.16, s * 0.68);
  ctx.fillRect(cx - s * 0.08, cy + s * 0.32, s * 0.16, s * 0.16);
  ctx.globalAlpha = 1;
}

/** A fireball where it is: a ball of flame with its tail behind it and a glow. */
function drawFireball(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  a: AttackObject,
  tick: number,
  time: number,
): void {
  if (!attackBox(a, tick, BOX)) return;
  attackBox(a, Math.max(a.from, tick - 3), BEFORE);
  const x = (BOX.x1 + BOX.x2) / 2;
  const y = (BOX.y1 + BOX.y2) / 2;
  // Its tail streams back along its way.
  let dx = x - (BEFORE.x1 + BEFORE.x2) / 2;
  let dy = y - (BEFORE.y1 + BEFORE.y2) / 2;
  const d = Math.hypot(dx, dy) || 1;
  dx /= d;
  dy /= d;
  glow(ctx, view, x, y, 2.4, FIRE[1], 0.9);
  const r = 0.48;
  for (let layer = 0; layer < 3; layer++) {
    const rr = r * (1 - layer * 0.28);
    const tail = (1.4 - layer * 0.35) * (1 + 0.12 * Math.sin(time * 37 + layer));
    ctx.beginPath();
    ctx.moveTo(view.sx(x + dy * rr), view.sy(y - dx * rr));
    ctx.quadraticCurveTo(
      view.sx(x - dx * tail * 0.5 + dy * rr * 0.6),
      view.sy(y - dy * tail * 0.5 - dx * rr * 0.6),
      view.sx(x - dx * tail),
      view.sy(y - dy * tail),
    );
    ctx.quadraticCurveTo(
      view.sx(x - dx * tail * 0.5 - dy * rr * 0.6),
      view.sy(y - dy * tail * 0.5 + dx * rr * 0.6),
      view.sx(x - dy * rr),
      view.sy(y + dx * rr),
    );
    ctx.arc(view.sx(x), view.sy(y), view.len(rr), 0, TAU);
    ctx.fillStyle = FIRE[layer]!;
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(view.sx(x), view.sy(y), view.len(r * 0.3), 0, TAU);
  ctx.fillStyle = FIRE[3];
  ctx.fill();
}

/**
 * A flame: out of the ground (tongues of fire licking up from its base, a little taller than it
 * kills) or pouring down from the sky (a column from above the view down to its bottom, a splash
 * there); dying down for a moment after it is gone.
 */
function drawFlame(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  a: AttackObject,
  tick: number,
  time: number,
): void {
  let fade = 1;
  if (!attackBox(a, tick, BOX)) {
    // Gone: its last box, dying down.
    attackBox(a, a.to - 1, BOX);
    fade = 1 - (tick - a.to) / FADE_TICKS;
    if (fade <= 0) return;
  }
  const w = BOX.x2 - BOX.x1;
  const mid = (BOX.x1 + BOX.x2) / 2;
  if (a.grows === 'down') {
    const top = Math.max(BOX.y2, view.top + 0.5);
    const bottom = BOX.y1 + (1 - fade) * 2;
    for (let layer = 0; layer < 3; layer++) {
      const half = (w / 2) * (1.25 - layer * 0.32) * fade;
      ctx.beginPath();
      ctx.moveTo(view.sx(mid - half), view.sy(top));
      const steps = 6;
      for (let k = 0; k <= steps; k++) {
        const y = top + ((bottom - top) * k) / steps;
        const wave = Math.sin(time * 23 + k * 1.7 + layer) * 0.08;
        ctx.lineTo(view.sx(mid - half - wave), view.sy(y));
      }
      ctx.quadraticCurveTo(
        view.sx(mid),
        view.sy(bottom - half * 0.9),
        view.sx(mid + half),
        view.sy(bottom),
      );
      for (let k = steps; k >= 0; k--) {
        const y = top + ((bottom - top) * k) / steps;
        const wave = Math.sin(time * 19 + k * 1.3 - layer) * 0.08;
        ctx.lineTo(view.sx(mid + half + wave), view.sy(y));
      }
      ctx.closePath();
      ctx.fillStyle = FIRE[layer]!;
      ctx.fill();
    }
    glow(ctx, view, mid, bottom, 2.2 * fade, FIRE[1], 0.8 * fade);
    return;
  }
  // Out of the ground: tongues of flame from its base up past its top.
  const height = (BOX.y2 - BOX.y1) * fade;
  const base = BOX.y1;
  glow(ctx, view, mid, base + height * 0.4, Math.max(1.6, height * 1.8), FIRE[0], 0.75 * fade);
  for (let layer = 0; layer < 3; layer++) {
    const half = (w / 2 + 0.18) * (1 - layer * 0.28);
    const tall = (height + 0.35) * (1 - layer * 0.22);
    ctx.beginPath();
    ctx.moveTo(view.sx(mid - half), view.sy(base));
    const tongues = 3;
    for (let k = 0; k < tongues; k++) {
      const x1 = mid - half + ((2 * half) / tongues) * k;
      const x2 = x1 + (2 * half) / tongues;
      const lick = tall * (0.75 + 0.25 * Math.sin(time * (17 + 3 * k) + layer * 1.3 + k));
      const tip = k === 1 ? lick : lick * 0.8;
      ctx.quadraticCurveTo(
        view.sx(x1),
        view.sy(base + tip * 0.6),
        view.sx((x1 + x2) / 2),
        view.sy(base + tip),
      );
      ctx.quadraticCurveTo(
        view.sx(x2),
        view.sy(base + tip * 0.6),
        view.sx(x2),
        view.sy(base + tip * 0.3),
      );
    }
    ctx.lineTo(view.sx(mid + half), view.sy(base));
    ctx.closePath();
    ctx.fillStyle = FIRE[layer]!;
    ctx.fill();
  }
}

/** A laser beam from the robot's muzzle (or its own start) across the view, flickering. */
function drawLaser(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  a: AttackObject,
  state: AttackDrawState,
): void {
  const { tick, time } = state;
  const fade = tick < a.to ? 1 : 1 - (tick - a.to) / FADE_TICKS;
  if (fade <= 0) return;
  const y = view.sy(a.y + a.h / 2);
  const from = Math.max(view.x - 4, view.sx(Math.max(a.x, state.muzzleX ?? a.x)));
  const to = Math.min(view.x + view.w + 4, view.sx(a.x + a.w));
  if (to <= from) return;
  const flicker = 0.85 + 0.15 * Math.sin(time * 61);
  const outer = view.len(0.5) * flicker * fade;
  const inner = view.len(0.14) * flicker * fade;
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = rgba(LASER.glow, 0.35 * fade);
  ctx.fillRect(from, y - outer / 2, to - from, outer);
  ctx.fillStyle = rgba(LASER.glow, 0.85 * fade);
  ctx.fillRect(from, y - outer / 4, to - from, outer / 2);
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = rgba(LASER.core, fade);
  ctx.fillRect(from, y - inner / 2, to - from, inner);
}

/** A rocket flying left: a body with a red nose and fins, its flame and smoke behind it. */
function drawRocket(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  a: AttackObject,
  tick: number,
  time: number,
): void {
  if (!attackBox(a, tick, BOX)) return;
  const x1 = BOX.x1 - 0.15;
  const x2 = BOX.x2 + 0.15;
  const y = (BOX.y1 + BOX.y2) / 2;
  const r = (BOX.y2 - BOX.y1) / 2 + 0.08;
  // Its flame, behind (right).
  const flame = 0.7 + 0.25 * Math.sin(time * 53);
  for (let layer = 0; layer < 2; layer++) {
    const rr = r * (0.8 - layer * 0.35);
    ctx.beginPath();
    ctx.moveTo(view.sx(x2), view.sy(y + rr));
    ctx.lineTo(view.sx(x2 + flame * (1 - layer * 0.4)), view.sy(y));
    ctx.lineTo(view.sx(x2), view.sy(y - rr));
    ctx.closePath();
    ctx.fillStyle = FIRE[layer + 1]!;
    ctx.fill();
  }
  glow(ctx, view, x2 + 0.3, y, 1.6, FIRE[1], 0.7);
  // Fins.
  ctx.beginPath();
  ctx.moveTo(view.sx(x2 - 0.35), view.sy(y + r));
  ctx.lineTo(view.sx(x2 + 0.1), view.sy(y + r + 0.28));
  ctx.lineTo(view.sx(x2 + 0.05), view.sy(y + r - 0.05));
  ctx.moveTo(view.sx(x2 - 0.35), view.sy(y - r));
  ctx.lineTo(view.sx(x2 + 0.1), view.sy(y - r - 0.28));
  ctx.lineTo(view.sx(x2 + 0.05), view.sy(y - r + 0.05));
  ctx.fillStyle = ROBOT.eye;
  ctx.fill();
  // The body and its red nose (pointing left, the way it flies).
  ctx.beginPath();
  ctx.moveTo(view.sx(x2), view.sy(y + r));
  ctx.lineTo(view.sx(x1 + r), view.sy(y + r));
  ctx.quadraticCurveTo(view.sx(x1 - r * 0.6), view.sy(y), view.sx(x1 + r), view.sy(y - r));
  ctx.lineTo(view.sx(x2), view.sy(y - r));
  ctx.closePath();
  ctx.fillStyle = '#e8edf3';
  ctx.fill();
  ctx.strokeStyle = ROBOT.ink;
  ctx.lineWidth = Math.max(1.5, view.len(0.06));
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(view.sx(x1 + r * 1.2), view.sy(y + r));
  ctx.quadraticCurveTo(view.sx(x1 - r * 0.6), view.sy(y), view.sx(x1 + r * 1.2), view.sy(y - r));
  ctx.closePath();
  ctx.fillStyle = ROBOT.eye;
  ctx.fill();
}

/** Where the boss crashes (world point, for its smoke and stars): its chest, in view. */
export function bossMiddle(
  pose: BossPose,
  out: { x: number; y: number },
): { x: number; y: number } {
  out.x = pose.x - (pose.kind === 'dragon' ? 0.9 : 0.8);
  out.y = pose.kind === 'dragon' ? DRAGON_Y + 0.6 : 3;
  return out;
}

/** A boss's look for tools (the harness): its kind's main colour. */
export function bossColor(kind: BossKind): string {
  return kind === 'dragon' ? DRAGON.body : ROBOT.steel;
}
